import { Box } from '@mui/material';
import { Editor } from '../../../../../components/wysiwyg/Editor';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from '../../../../../store/snackbar';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useDebounceEffect } from '../../../../../utils/useDebounceEffect';
import { z } from 'zod';

const activityNotesQuery = graphql(`
	query journeyDetail_getActivityNotes($activityId: UUID!) {
		getActivity(by: { id: $activityId }) {
			notes
		}
	}
`);

const updateNotesMutation = graphql(`
	mutation journeyDetail_updateNotes($id: UUID!, $notes: String) {
		updateActivity(by: { id: $id }, data: { notes: $notes }) {
			ok
			errorMessage
		}
	}
`);

export const notesEditDrawerSchema = z.object({
	activityId: z.string(),
});

export const NotesEditDrawer: FC<z.infer<typeof notesEditDrawerSchema>> = ({ activityId }) => {
	const intl = useIntl();
	const [notes, setNotes] = useState('');
	const [isEditorReady, setIsEditorReady] = useState(false);
	const { openSnackbar } = useSnackbar();

	const { data, isPending } = useApiQuery(activityNotesQuery, {
		variables: { activityId },
	});

	const { mutate: updateActivitNotes } = useApiMutation(updateNotesMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'widget.notes.update.error' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			}),
	});

	useEffect(() => {
		if (!isPending) {
			setNotes(data?.getActivity?.notes ?? '');
			setIsEditorReady(true);
		}
		// excluding other dependencies as latest data are passed in when isPending changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPending]);

	useDebounceEffect(() => {
		if (isEditorReady && data?.getActivity?.notes !== notes) {
			updateActivitNotes({ id: activityId, notes });
		}
	}, [notes, data, isEditorReady]);

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.detail.widget.notes.title' })} />
			<Box width={'100%'}>
				{isEditorReady && <Editor placeholder={'...'} state={notes} setState={(value: string) => setNotes(value)} />}
			</Box>
		</DrawerWrapper>
	);
};
